import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Person from "@mui/icons-material/Person";
import SupervisorAccount from "@mui/icons-material/SupervisorAccount";
import Group from "@mui/icons-material/Group";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { DatePicker } from "@mui/x-date-pickers-pro";
import layout from "../../../actprep-ui/constants/layout";
import PaginatedTable from "../../../actprep-ui/components/PaginatedTable";
import { apiUrls } from "../../../actprep-ui/utils/urls";
import { dataGridNestedColumnGetter } from "../../../actprep-ui/utils/tables";
import {
  sexyBooleanColumn,
  sortingModes,
} from "../../../actprep-ui/constants/dataGrid";
import ContentBox from "../../../actprep-ui/components/ContentBox";
import { teamManagementFilters } from "../../../constants/filters";
import { useFilters, useDrawer } from "../../../actprep-ui/hooks/ui";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import Settings from "@mui/icons-material/Settings";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import Button from "@mui/material/Button";
import CoachManagement from "../../../components/CoachManagement";
import TransferStudentsForm from "../../../components/TransferStudentsForm";
import { useGet } from "../../../actprep-ui/hooks/api";
import useDisclosure from "../../../actprep-ui/hooks/ui/useDisclosure";
import dayjs from "dayjs";

export default function TeamManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedItems, setExpandedItems] = useState([]);
  const [reload, setReload] = useState(false);
  const processedRefreshTriggers = useRef(new Set());
  const sidebarWidth = 20;

  const { open, setOpen: setFiltersDrawerOpen, Drawer } = useDrawer();
  const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
    name: "Team Management Filters",
    saveState: true,
    filtersConfig: teamManagementFilters,
    handleClose: () => setFiltersDrawerOpen(false),
  });
  const transferStudentsDisclosure = useDisclosure();
  const selectedCoachId = searchParams.get("coachId");
  const refreshTrigger = searchParams.get("refresh");

  // Get asOf date from URL params or default to today
  const asOfParam = searchParams.get("asOf");
  const [asOfDate, setAsOfDate] = useState(
    asOfParam ? dayjs(asOfParam) : dayjs()
  );
  const { data: hierarchyResponse, refetch: refetchHierarchy } = useGet(
    apiUrls.coaching.hierarchy(),
    {
      params: {
        search_params: {
          as_of: asOfDate.format("YYYY-MM-DD"),
        },
      },
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    }
  );

  // Refresh hierarchy when returning from coach management with a refresh trigger
  useEffect(() => {
    if (
      !selectedCoachId &&
      refreshTrigger &&
      !processedRefreshTriggers.current.has(refreshTrigger)
    ) {
      // We've returned to the main team management view with a new refresh trigger
      processedRefreshTriggers.current.add(refreshTrigger);
      refetchHierarchy();

      // Clean up the refresh parameter from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("refresh");
      setSearchParams(newParams, { replace: true });
    }
  }, [
    selectedCoachId,
    refreshTrigger,
    refetchHierarchy,
    searchParams,
    setSearchParams,
  ]);

  const hierarchyData = hierarchyResponse?.data;

  // If a coach is selected, show the coach management interface
  if (selectedCoachId) {
    return (
      <CoachManagement
        coachId={selectedCoachId}
        onCoachUpdated={() => setReload(true)}
      />
    );
  }

  const handleManageCoach = (coach) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("coachId", coach.uuid);
    setSearchParams(newParams);
  };

  const handleAsOfDateChange = (newDate) => {
    setAsOfDate(newDate);
    const newParams = new URLSearchParams(searchParams);
    if (newDate) {
      newParams.set("asOf", newDate.format("YYYY-MM-DD"));
    } else {
      newParams.delete("asOf");
    }
    setSearchParams(newParams);
  };

  // Create a map of role assignments for easy lookup
  const nodesMap =
    hierarchyData?.nodes?.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {}) || {};

  // Transform hierarchy data for RichTreeView
  const transformTreeData = (forest) => {
    if (!forest || !Array.isArray(forest)) return [];

    return forest.map((node) => ({
      id: node.id,
      label: getNodeDisplayName(nodesMap[node.id]),
      children: node.children ? transformTreeData(node.children) : [],
    }));
  };

  const getNodeDisplayName = (node) => {
    if (!node) return "Unknown";
    const coach = node.coach;
    const isActive = node.active_as_of ? "Active" : "Inactive";
    return `${coach?.name || "Unknown Coach"} - ${node.role} (${isActive})`;
  };

  const renderTreeItemContent = (nodeId) => {
    const node = nodesMap[nodeId];
    if (!node) return null;

    const coach = node.coach;
    const isActive = node.active_as_of;
    const endDate = node.end_date
      ? dayjs(node.end_date).format("MM/DD/YYYY")
      : null;

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
        <Avatar
          sx={{ width: 32, height: 32 }}
          src={coach?.user_profile?.profile_pic}
        >
          {coach?.name
            ?.split(" ")
            .map((n) => n[0])
            .join("") || "?"}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {coach?.name || "Unknown Coach"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={node.role}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={isActive ? "Active" : "Inactive"}
              size="small"
              color={isActive ? "success" : "default"}
              variant={isActive ? "filled" : "outlined"}
            />
            {coach?.team_name && (
              <Chip label={coach.team_name} size="small" variant="outlined" />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {dayjs(node.start_date).format("MM/DD/YYYY")} -{" "}
            {endDate || "Ongoing"}
          </Typography>
        </Box>
      </Stack>
    );
  };

  const treeData = transformTreeData(hierarchyData?.forest || []);

  const columns = [
    {
      field: "avatar",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const coach = params.row;
        const initials = [coach.user?.first_name, coach.user?.last_name]
          .filter(Boolean)
          .map((name) => name.charAt(0))
          .join("")
          .toUpperCase();

        return (
          <Avatar
            src={coach.user_profile?.profile_pic}
            sx={{ width: 32, height: 32, my: 1 }}
          >
            {initials}
          </Avatar>
        );
      },
    },
    {
      field: "user.first_name",
      headerName: "First Name",
      width: 150,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "user.last_name",
      headerName: "Last Name",
      width: 150,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 150,
    },
    {
      field: "team_name",
      headerName: "Team",
      width: 150,
    },
    {
      field: "is_active",
      headerName: "Active",
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="manage"
          icon={<Settings />}
          label="Manage Roles & Hierarchy"
          onClick={() => handleManageCoach(params.row)}
        />,
      ],
    },
  ];

  return (
    <Grid
      sx={{
        mt: `${layout.navBarHeight + 1}rem`,
        mb: 4,
      }}
      size={12}
    >
      <Typography variant="h5" fontWeight={"bold"} my={2} color={"primary"}>
        Team Management
      </Typography>

      {/* Actions Section */}
      <ContentBox sx={{ mb: 3 }}>
        <Card>
          <CardHeader
            title={
              <Stack direction="row" spacing={2} alignItems="center">
                <Settings color="primary" />
                <Typography variant="h6">Actions</Typography>
              </Stack>
            }
            subheader="Team management actions and operations"
          />
          <CardContent>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<SwapHoriz />}
                onClick={transferStudentsDisclosure.onOpen}
              >
                Transfer Students
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </ContentBox>

      {/* Coaching Hierarchy Visualization */}
      <ContentBox sx={{ mb: 3 }}>
        <Card>
          <CardHeader
            title={
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Group color="primary" />
                  <Typography variant="h6">Coaching Hierarchy</Typography>
                  <Chip
                    label={`As of ${asOfDate.format("MMM D, YYYY")}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
                <DatePicker
                  label="As of Date"
                  value={asOfDate}
                  onChange={handleAsOfDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </Stack>
            }
            subheader={
              hierarchyData ? (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{hierarchyData.nodes?.length || 0}</strong> active
                    roles
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{hierarchyData.edges?.length || 0}</strong>{" "}
                    reporting relationships
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{hierarchyData.forest?.length || 0}</strong>{" "}
                    hierarchy trees
                  </Typography>
                </Stack>
              ) : (
                "Loading hierarchy data..."
              )
            }
          />
          <CardContent>
            {treeData.length > 0 ? (
              <Box sx={{ maxHeight: 600, overflow: "auto" }}>
                <RichTreeView
                  items={treeData}
                  expandedItems={expandedItems}
                  onExpandedItemsChange={(event, itemIds) =>
                    setExpandedItems(itemIds)
                  }
                  slots={{
                    expandIcon: ChevronRight,
                    collapseIcon: ExpandMore,
                    item: (props) => (
                      <TreeItem
                        {...props}
                        label={renderTreeItemContent(props.itemId)}
                      />
                    ),
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SupervisorAccount
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="body1" color="text.secondary">
                  No active coaching hierarchy found for{" "}
                  {asOfDate.format("MMM D, YYYY")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Role assignments and reporting relationships will appear here
                  when available.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </ContentBox>

      {/* Coach Profiles Table */}
      <ContentBox>
        <Card
          style={{
            transition: "margin 0.3s ease",
          }}
        >
          <CardHeader
            title={
              <Stack direction="row" spacing={2} alignItems="center">
                <Person color="primary" />
                <Typography variant="h6">Coach Profiles</Typography>
              </Stack>
            }
            subheader="Manage individual coach roles and reporting relationships"
          />
          <CardContent>
            <PaginatedTable
              searchable
              tableName={"TeamManagement"}
              apiUrl={apiUrls.coaching.profiles()}
              columns={columns}
              urlParams={{
                keep_related: true,
                search_params: getFilterParams(),
              }}
              sortingMode={sortingModes.SERVER}
              disableCellFocus
              onFiltersClick={() => setFiltersDrawerOpen(!open)}
              FiltersAppliedList={FiltersAppliedList}
              reload={reload}
              setReload={setReload}
            />
          </CardContent>
        </Card>
      </ContentBox>

      <Drawer
        anchor="right"
        open={open}
        variant={"temporary"}
        sx={{
          width: `${sidebarWidth}rem`,
        }}
      >
        <Filters />
      </Drawer>

      {/* Transfer Students Dialog */}
      <TransferStudentsForm
        isOpen={transferStudentsDisclosure.isOpen}
        onClose={transferStudentsDisclosure.onClose}
      />
    </Grid>
  );
}
